import React, { useState, useEffect, useCallback } from 'react';
import { useResponsive } from '../hooks/useResponsive';
import { spocsFromCompanies } from '../utils/filterUtils';
import { Info, X, UserCheck, CheckCircle, TrendingUp, Trash2, Plus, Clock, Save, Send, Eye, Ban } from 'lucide-react';
import { DateInput, formatDisplayDate, formatTimestampDate, toYMD } from './DateFilter';

const priorityColors = {
    High: { background: '#fee2e2', color: '#b91c1c' },
    Medium: { background: '#fef3c7', color: '#92400e' },
    Low: { background: '#dcfce7', color: '#166534' }
};

const statusColors = {
    'Assigned To': { background: '#dbeafe', color: '#1e40af' },
    'Assigned': { background: '#dbeafe', color: '#1e40af' },
    'Pending': { background: '#e0e7ff', color: '#3730a3' },
    'Work In Progress': { background: '#fef3c7', color: '#92400e' },
    'ToSubmit': { background: '#fde68a', color: '#92400e' },
    'Submitted': { background: '#c7d2fe', color: '#3730a3' },
    'Completed': { background: '#fef08a', color: '#854d0e' } // matches "Completed" badge shown in mockup
};

const REMARK_OPTIONS = ['Interested', 'Not Interested', 'Not Eligible', 'Not Picked Up', 'Not Reachable', 'Call Later', '60 Days Notice', '90 Days Notice', "Number Doesn't Exist",];

const infoLabelStyle = { fontSize: '15px', color: '#64748b', fontWeight: '600', display: 'block', marginBottom: '2px' };
const sectionBoxStyle = { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px', padding: '16px' };
const tableInputStyle = { width: '100%', padding: '7px 9px', border: '1px solid #e2e8f0', borderRadius: '5px', fontSize: '13px', boxSizing: 'border-box', outline: 'none' };
const lockedTableInputStyle = { ...tableInputStyle, background: '#f3f4f6', color: '#6b7280', cursor: 'not-allowed' };
const remarkSelectStyle = { ...tableInputStyle, background: '#fff' };

const cdFieldStyle = { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', outline: 'none' };
const cdLabelStyle = { fontSize: '11px', fontWeight: '600', color: '#374151', marginBottom: '5px', display: 'block' };
const cdFieldWrap = { display: 'flex', flexDirection: 'column' };

const selectStyle = { width: '100%', padding: '9px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', outline: 'none', background: '#fff' };
const textareaStyle = { width: '100%', padding: '9px 10px', border: '1px solid #d1d5db', borderRadius: '6px', fontSize: '13px', boxSizing: 'border-box', outline: 'none', resize: 'vertical' };
const API = process.env.REACT_APP_BACKEND_API_URL;

const TaskDetailModel = ({ task, onClose, onUpdateTaskStatus, currentUser, onTaskUpdated }) => {
    // Suggestions for the Client SPOC field: this task's company's SPOCs, from /api/companies.
    const [companyMaster, setCompanyMaster] = useState([]);
    useEffect(() => {
        fetch(`${API}/api/companies`)
            .then(res => res.json())
            .then(data => setCompanyMaster(Array.isArray(data) ? data : []))
            .catch(err => console.error('Failed to fetch companies:', err));
    }, []);
    const { isMobile } = useResponsive();
    const todayStr = () => toYMD(new Date());

    const blankRow = () => ({
        id: Date.now(),
        entry_date: todayStr(),
        name: '',
        number: '',
        remark: ''
    });

    // Rows currently being typed, not yet saved to the DB.
    const [draftRows, setDraftRows] = useState([blankRow()]);
    // Previously saved entries for this task, fetched from kpi_drafts.
    const [savedLog, setSavedLog] = useState([]);
    const [savingKpi, setSavingKpi] = useState(false);
    const [kpiError, setKpiError] = useState('');

    // Item #4: recruiter reassignment — only Team Lead/Admin see the control
    // at all, and only when status allows it (Pending, Assigned, Completed —
    // never WIP or Ready To Submit, enforced here AND by the backend).
    const isTeamLeadOrAdmin = currentUser?.role === 'teamLead' || currentUser?.role === 'admin';
    const [recruiterOptions, setRecruiterOptions] = useState([]);
    const [recruiterChoice, setRecruiterChoice] = useState(task?.assigned_to || '');

    useEffect(() => {
        setRecruiterChoice(task?.assigned_to || '');
        if (!isTeamLeadOrAdmin) {
            setRecruiterOptions([]);
            return;
        }

        let cancelled = false; const fetchAssignees = async () => {
            try {
                const [recruiterRes, teamLeadRes] = await Promise.all([
                    fetch(`${API}/api/users?role=recruiter`),
                    fetch(`${API}/api/users?role=teamLead`)
                ]);

                if (!recruiterRes.ok || !teamLeadRes.ok) {
                    throw new Error('Failed to fetch users');
                }

                const [recruiters, teamLeads] = await Promise.all([
                    recruiterRes.json(),
                    teamLeadRes.json()
                ]);

                if (cancelled) return;

                const names = [
                    ...(Array.isArray(recruiters) ? recruiters : []),
                    ...(Array.isArray(teamLeads) ? teamLeads : [])
                ]
                    .map(user => user.name?.trim())
                    .filter(Boolean);

                // Keep the currently assigned person visible as well.
                if (task?.assigned_to?.trim()) {
                    names.push(task.assigned_to.trim());
                }

                setRecruiterOptions([...new Set(names)]);
            } catch (err) {
                console.error('Failed to fetch reassignment users:', err);
            }
        };

        fetchAssignees();

        return () => {
            cancelled = true;
        };
    }, [task?.id, task?.assigned_to, isTeamLeadOrAdmin]);

    const statusLowerForGuard = (task?.status || '').trim().toLowerCase();
    const canChangeRecruiter = isTeamLeadOrAdmin && !['work in progress', 'ready to submit'].includes(statusLowerForGuard);

    const handleChangeRecruiter = async () => {
        if (!canChangeRecruiter || recruiterChoice === task.assigned_to) return;
        try {
            // Wait for PATCH /api/tasks/:id to complete.
            // handleUpdateTaskStatus already refreshes the Kanban via fetchTasks().
            await onUpdateTaskStatus(task.id, null, '', '', recruiterChoice);
            // Keep the local dropdown synchronized with the successful reassignment.
            setRecruiterChoice(recruiterChoice);
        } catch (err) {
            console.error('Failed to reassign task:', err);
        }
    };

    // Item #5: Admin-only full task edit — everything except Task ID.
    // Deliberately separate from Change Recruiter / Kanban transitions /
    // Reassign Task above, so none of that history is touched by this.
    const isAdmin = currentUser?.role === 'admin';
    const [showEditTask, setShowEditTask] = useState(false);
    const [editTaskForm, setEditTaskForm] = useState({ title: '', company: '', client_spoc: '', instructions: '', assigned_date: '', job_role: '', job_id: '' });
    const [editTaskError, setEditTaskError] = useState('');
    const [savingEditTask, setSavingEditTask] = useState(false);
    // Job Role isn't its own field on a task — it comes from whichever job the
    // task is linked to — so editing it means picking a different Open job at
    // the same company, same dependent-selection pattern as task creation.
    const [openJobsForEdit, setOpenJobsForEdit] = useState([]);

    useEffect(() => {
        if (!isAdmin) return;
        fetch(`${API}/api/job-requirements`)
            .then(res => res.json())
            .then(data => setOpenJobsForEdit((data || []).filter(j => j.status === 'Open')))
            .catch(err => console.error('Failed to fetch job requirements:', err));
    }, [isAdmin]);

    const jobRoleOptionsForEdit = openJobsForEdit.filter(j => (j.company_name || '').trim().toLowerCase() === (editTaskForm.company || '').trim().toLowerCase());

    const openEditTask = () => {
        setEditTaskForm({
            title: task.title || '', company: task.company || '', client_spoc: task.client_spoc || '',
            instructions: task.instructions || '', assigned_date: task.assigned_date ? task.assigned_date.split('T')[0] : '',
            job_role: task.job_role || '', job_id: task.job_id || ''
        });
        setEditTaskError('');
        setShowEditTask(true);
    };

    // Changing Company invalidates whatever Job Role had been resolved from
    // the previous company's jobs — same as task creation.
    const handleEditCompanyChange = (value) => setEditTaskForm(prev => ({ ...prev, company: value, job_role: '', job_id: '' }));

    // Selecting a Job Role resolves to one specific Open job at the current
    // company, and carries that job's Company + Client SPOC along with it.
    const handleEditJobRoleChange = (selectedRole) => {
        const matchedJob = jobRoleOptionsForEdit.find(j => j.job_role === selectedRole);
        setEditTaskForm(prev => ({
            ...prev,
            job_role: selectedRole,
            job_id: matchedJob ? matchedJob.job_id : '',
            company: matchedJob ? matchedJob.company_name : prev.company,
            client_spoc: matchedJob?.client_spoc || prev.client_spoc
        }));
    };

    const handleSaveEditTask = async () => {
        setEditTaskError('');
        setSavingEditTask(true);
        try {
            const response = await fetch(`${API}/api/tasks/${task.id}/edit`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ ...editTaskForm, requester_role: currentUser?.role, updated_by: currentUser?.name })
            });
            const data = await response.json();
            if (!response.ok) {
                setEditTaskError(data.error || 'Failed to save changes.');
                return;
            }
            setShowEditTask(false);
            onTaskUpdated?.(); // Item #5: reflect immediately — Kanban, Task Details, and Analytics all refetch from the same API.
        } catch (err) {
            console.error('Failed to save task edits:', err);
            setEditTaskError('Server connection failed.');
        } finally {
            setSavingEditTask(false);
        }
    };

    // Submission form — only used/shown while the task is at the ToSubmit stage.
    const [submissionDescription, setSubmissionDescription] = useState('');
    const [submittedTo, setSubmittedTo] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');
    const [approving, setApproving] = useState(false);
    const [approveError, setApproveError] = useState('');
    // Every Team Lead from the existing users data — the "Submit To" choices (plus Client).
    const [teamLeadNames, setTeamLeadNames] = useState([]);
    useEffect(() => {
        let cancelled = false;
        fetch(`${API}/api/users?role=teamLead`)
            .then(res => res.json())
            .then(data => { if (!cancelled && Array.isArray(data)) setTeamLeadNames(data.map(u => u.name).filter(Boolean)); })
            .catch(err => console.error('Failed to fetch team leads:', err));
        return () => { cancelled = true; };
    }, []);

    // Candidate Details modal — opened per KPI log row marked "Interested".
    const [candidateModalRow, setCandidateModalRow] = useState(null); // the kpi_drafts row, or null
    const [candidateForm, setCandidateForm] = useState(null);
    const [savingCandidate, setSavingCandidate] = useState(false);
    const [candidateError, setCandidateError] = useState('');

    // The job requirement this task is linked to (via task.job_id), used to
    // autofill Skill (from job_role) and Vendor Name/SPOC (from company_name/
    // client_spoc) when opening the Candidate Details modal.
    const [linkedJob, setLinkedJob] = useState(null);
    const [showJobDetails, setShowJobDetails] = useState(false);

    const [history, setHistory] = useState([]);
    const [loadingHistory, setLoadingHistory] = useState(false);

    const fetchHistory = useCallback(() => {
        if (!task) return;
        setLoadingHistory(true);
        fetch(`${API}/api/tasks/${task.id}/history`)
            .then(res => res.json())
            .then(data => setHistory(data))
            .catch(err => console.error('Failed to fetch task history:', err))
            .finally(() => setLoadingHistory(false));
    }, [task]);

    const fetchKpiLog = useCallback(() => {
        if (!task) return;
        fetch(`${API}/api/tasks/${task.id}/kpi-drafts`)
            .then(res => res.json())
            .then(data => setSavedLog(data))
            .catch(err => console.error('Failed to fetch KPI log:', err));
    }, [task]);

    const fetchLinkedJob = useCallback(() => {
        if (!task?.job_id) {
            setLinkedJob(null);
            return;
        }
        fetch(`${API}/api/job-requirements/by-job-id/${encodeURIComponent(task.job_id)}`)
            .then(res => res.ok ? res.json() : null)
            .then(data => setLinkedJob(data))
            .catch(err => console.error('Failed to fetch linked job requirement:', err));
    }, [task]);

    // Details already live on the same kpi_drafts row (no separate fetch needed).
    // Skill/Vendor Name/Client SPOC default to the linked job's values, but only
    // when this row hasn't already been given its own values — so re-opening an
    // already-completed entry never silently overwrites what was saved before.
    const openCandidateModal = (row) => {
        setCandidateModalRow(row);
        setCandidateError('');
        setCandidateForm({
            skill: row.skill || linkedJob?.job_role || '',
            email: row.email || '',
            location: row.location || '', current_employer: row.current_employer || '',
            designation: row.designation || '', total_experience_years: row.total_experience_years || '',
            relevant_experience_years: row.relevant_experience_years || '',
            notice_period: row.notice_period || '', education: row.education || '',
            vendor_name: row.vendor_name || linkedJob?.company_name || '',
            client_spoc: row.client_spoc || linkedJob?.client_spoc || '',
            evanti_spoc: row.evanti_spoc || linkedJob?.evanti_spoc || '',
            bill_rate: row.bill_rate || linkedJob?.bill_rate || '',
            current_package: row.current_package || '', expected_package: row.expected_package || '',
            details_remarks: row.details_remarks || ''
        });
    };

    const closeCandidateModal = () => {
        setCandidateModalRow(null);
        setCandidateForm(null);
        setCandidateError('');
    };

    const handleCandidateFieldChange = (field) => (e) => {
        setCandidateForm(prev => ({ ...prev, [field]: e.target.value }));
    };

    const handleSaveCandidateDetails = async () => {
        setCandidateError('');
        if (!candidateForm.email.trim() || !candidateForm.skill.trim()) {
            setCandidateError('Skill and Email are required.');
            return;
        }
        setSavingCandidate(true);
        try {
            const response = await fetch(`${API}/api/tasks/${task.id}/candidates/${candidateModalRow.id}/details`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(candidateForm)
            });
            const data = await response.json();
            if (!response.ok) {
                setCandidateError(data.error || 'Failed to save candidate details.');
                return;
            }
            fetchKpiLog(); // refresh so the log reflects the saved details immediately
            closeCandidateModal();
        } catch (err) {
            console.error('Failed to save candidate details:', err);
            setCandidateError('Server connection failed. Ensure backend is running on port correctly.');
        } finally {
            setSavingCandidate(false);
        }
    };

    // Item #5: duplicate Candidate Name + Number, possibly under a different
    // Job Role. `duplicateCheck` holds the popup's data while it's open.
    const [duplicateCheck, setDuplicateCheck] = useState(null);
    const [checkingDuplicate, setCheckingDuplicate] = useState(false);

    useEffect(() => {
        if (task) {
            fetchHistory();
            fetchKpiLog();
            fetchLinkedJob();
            setDraftRows([blankRow()]);
            setKpiError('');
            setSubmissionDescription('');
            setSubmittedTo('');
            setSubmitError('');
        }
    }, [task, fetchHistory, fetchKpiLog, fetchLinkedJob]);

    // Admin End Task form state (declared before the early return to keep hook order stable)
    const [showEndForm, setShowEndForm] = useState(false);
    const [endReasonInput, setEndReasonInput] = useState('');
    const [endingTask, setEndingTask] = useState(false);
    const [endError, setEndError] = useState('');

    if (!task) return null;

    const statusLower = (task.status || '').trim().toLowerCase();
    const isPendingStage = ['assigned', 'assigned to', 'pending', 'unassigned'].includes(statusLower);
    const isToSubmitStage = statusLower === 'tosubmit' || statusLower === 'to submit' || statusLower === 'ready to submit';
    const isAwaitingApproval = statusLower === 'ready to submit';
    // Submitted = the recruiter has filled Description + Submit To (the task still sits in Ready To Submit until approved).
    const isSubmitted = isAwaitingApproval && !!(task.submission_description && task.submitted_to);
    const isLocked = statusLower === 'completed'; // nothing editable at all
    // KPI entry is only allowed once the task has been Accepted (Work In Progress).
    // Locked before that (still Pending) and after (ToSubmit/Submitted/Completed).
    const kpiLocked = isPendingStage || isToSubmitStage || isAwaitingApproval || isLocked;
    // Add/Edit Details now shares the exact same window as basic KPI entry —
    // Work In Progress only. Once the task reaches Ready To Submit, KPI
    // details are frozen entirely; only the submission form is editable there.
    const detailsUnlocked = !kpiLocked;

    // Only the person the task was assigned_by (i.e. the Team Lead) can approve
    // a task sitting in "Submitted" status.
    // (Admin can always approve; backend enforces the same rule.)
    // The Team Lead the recruiter selected on submit can approve too.
    const sameName = (a, b) => !!a && !!b && String(a).trim().toLowerCase() === String(b).trim().toLowerCase();
    const isSelectedApprover = currentUser?.role === 'teamLead' && sameName(task.submitted_to, currentUser?.name) && !sameName(task.submitted_to, 'Client');
    const canApprove = isSubmitted && (currentUser?.name === task.assigned_by || currentUser?.role === 'admin' || isSelectedApprover);

    // Admin End Task
    const isAdminEnded = isLocked && !!task.admin_ended;
    const canEndTask = currentUser?.role === 'admin' && !isLocked;

    const addDraftRow = () => {
        setDraftRows(prev => [...prev, blankRow()]);
    };

    const removeDraftRow = (id) => {
        setDraftRows(prev => prev.filter(row => row.id !== id));
    };

    const updateDraftRow = (id, field, value) => {
        setDraftRows(prev => prev.map(row => row.id === id ? { ...row, [field]: value } : row));
    };

    const handleNumberBlur = async (rowId) => {
        const row = draftRows.find(r => r.id === rowId);
        if (!row || !row.name.trim() || !row.number.trim() || row.prefill) return;
        setCheckingDuplicate(true);
        try {
            const response = await fetch(`${API}/api/candidates/lookup?name=${encodeURIComponent(row.name.trim())}&number=${encodeURIComponent(row.number.trim())}`);
            const data = await response.json();
            if (data.found) {
                setDuplicateCheck({ rowId, existing_job_role: data.existing_job_role, existing_company: data.existing_company, existing_task_id: data.existing_task_id, details: data.details });
            }
        } catch (err) {
            console.error('Failed to check for an existing candidate:', err);
        } finally {
            setCheckingDuplicate(false);
        }
    };

    const handleUseDuplicateDetails = () => {
        if (!duplicateCheck) return;
        updateDraftRow(duplicateCheck.rowId, 'prefill', duplicateCheck.details);
        setDuplicateCheck(null);
    };

    const handleSkipDuplicateDetails = () => setDuplicateCheck(null);

    const handleSaveDraft = async () => {
        setKpiError('');

        // Only send rows that have at least one field filled in.
        const rowsToSave = draftRows.filter(r =>
            r.name.trim() || r.number.trim() || r.remark.trim()
        );

        if (rowsToSave.length === 0) {
            setKpiError('Fill in at least one field before saving.');
            return;
        }

        setSavingKpi(true);
        try {
            const response = await fetch(`${API}/api/tasks/${task.id}/kpi-drafts`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ rows: rowsToSave })
            });
            const data = await response.json();

            if (!response.ok) {
                setKpiError(data.error || 'Failed to save draft.');
                return;
            }

            setDraftRows([blankRow()]); // clear the input rows after a successful save
            fetchKpiLog(); // refresh the saved log below
        } catch (err) {
            console.error('Failed to save KPI draft:', err);
            setKpiError('Server connection failed. Ensure backend is running on port correctly.');
        } finally {
            setSavingKpi(false);
        }
    };

    const handleClose = () => {
        setDraftRows([blankRow()]); // discard any unsaved rows
        onClose();
    };

    // Options: Client + every Team Lead (+ the person who assigned this task when that is not a Team Lead,
    // e.g. an Admin). Identical for normal and reassigned tasks.
    const submitToOptions = (() => {
        const names = ['Client', ...teamLeadNames];
        if (task.assigned_by && !names.some(n => sameName(n, task.assigned_by))) names.push(task.assigned_by);
        return Array.from(new Set(names));
    })();

    // At least one candidate must be marked Interested AND have completed
    // details (skill + email) before this task can actually be submitted.
    const hasEligibleCandidate = savedLog.some(row =>
        !row.read_only && (row.remark || '').trim().toLowerCase() === 'interested' && row.skill && row.email
    );
    const canSubmit = submissionDescription.trim() !== '' && submittedTo !== '' && hasEligibleCandidate;

    const handleSubmitTask = async () => {
        if (!canSubmit) return;
        setSubmitError('');
        setSubmitting(true);
        try {
            // Status is omitted on purpose: the task stays in Ready To Submit (awaiting approval) and only the
            // approval action can complete it — the same call the Kanban card makes. The backend validates
            // description, recipient and candidate eligibility for normal and reassigned tasks alike.
            await onUpdateTaskStatus(task.id, null, submissionDescription.trim(), submittedTo);
            handleClose();
        } catch (err) {
            console.error('Failed to submit task:', err);
            setSubmitError(err?.message || 'Something went wrong submitting this task.');
        } finally {
            setSubmitting(false);
        }
    };

    const handleEndTask = async () => {
        setEndError('');
        setEndingTask(true);
        try {
            const response = await fetch(`${API}/api/tasks/${task.id}/end`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ reason: endReasonInput.trim() || undefined })
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                setEndError(data.error || 'Failed to end task.');
                return;
            }
            setShowEndForm(false);
            setEndReasonInput('');
            onTaskUpdated?.();
            handleClose();
        } catch (err) {
            console.error('Failed to end task:', err);
            setEndError('Server connection failed. Ensure backend is running on port correctly.');
        } finally {
            setEndingTask(false);
        }
    };

    const formatEndedAt = (value) => {
        const d = value ? new Date(value) : null;
        if (!d || isNaN(d.getTime())) return '-';
        const pad = (n) => String(n).padStart(2, '0');
        return `${pad(d.getDate())}-${pad(d.getMonth() + 1)}-${d.getFullYear()} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
    };

    const handleApprove = async () => {
        setApproveError('');
        setApproving(true);
        try {
            const response = await fetch(`${API}/api/tasks/${task.id}/approve`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ approved_by: currentUser?.name || 'Team Lead' })
            });
            const data = await response.json();

            if (!response.ok) {
                setApproveError(data.error || 'Failed to approve task.');
                return;
            }
            onTaskUpdated?.(); // refresh the shared task list so the Kanban reflects Completed right away
            handleClose();
        } catch (err) {
            console.error('Failed to approve task:', err);
            setApproveError('Server connection failed. Ensure backend is running on port correctly.');
        } finally {
            setApproving(false);
        }
    };

    const statusStyle = statusColors[task.status] || { background: '#e2e8f0', color: '#334155' };
    const priorityStyle = priorityColors[task.priority] || priorityColors.Medium;

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, padding: '20px', boxSizing: 'border-box' }}>
            <div style={{ background: '#fff', padding: isMobile ? '16px' : '28px', borderRadius: '14px', width: '100%', maxWidth: isMobile ? '95vw' : '900px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 10px 25px rgba(0,0,0,0.2)', position: 'relative', fontFamily: 'Inter, sans-serif' }}>
                <button onClick={handleClose} style={{ position: 'absolute', top: '20px', right: '20px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#666' }}>
                    <X size={20} />
                </button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#007bff', fontSize: '16px', fontWeight: 'bold' }}>
                        <Info size={20} /> Task Information Details
                    </div>
                    {isAdminEnded && (
                        <span data-testid="admin-ended-badge" style={{ background: '#6d28d9', color: '#fff', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '800', letterSpacing: '0.04em' }}>
                            {task?.ended_by}
                        </span>
                    )}
                    {isLocked && !isAdminEnded && (
                        <span style={{ background: '#dcfce7', color: '#166534', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '700' }}>
                            (Submitted & Locked)
                        </span>
                    )}
                    {isSubmitted && (
                        <span style={{ background: '#dbeafe', color: '#1e40af', padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '700' }}>
                            (Awaiting Approval)
                        </span>
                    )}
                </div>

                {/* Required header strip: Task ID, Job Role, Company Name, Current Status */}
                <div style={{
                    display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: isMobile ? '10px' : '20px',
                    background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '10px',
                    padding: '12px 16px', marginBottom: '20px'
                }}>
                    <div>
                        <span style={infoLabelStyle}>Task ID</span>
                        <strong style={{ fontSize: '14px', color: '#0f172a' }}>{task.display_id}</strong>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <div>
                            <span style={infoLabelStyle}>Job Role</span>
                            <strong style={{ fontSize: '14px', color: '#0f172a' }}>{linkedJob?.job_role || task.job_role || '-'}</strong>
                        </div>
                        <div>
                            <span style={infoLabelStyle}>Company Name</span>
                            <strong style={{ fontSize: '14px', color: '#0f172a' }}>{task.company}</strong>
                        </div>
                        {task.job_id && (
                            <button
                                type="button"
                                onClick={() => setShowJobDetails(true)}
                                title="View Job Details"
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '4px', background: '#eff6ff', color: '#1e40af',
                                    border: 'none', padding: '5px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer'
                                }}
                            >
                                <Eye size={12} /> View Job Details
                            </button>
                        )}
                    </div>
                    <div>
                        <span style={infoLabelStyle}>Current Status</span>
                        <span style={{ ...statusStyle, padding: '3px 10px', borderRadius: '12px', fontSize: '12px', fontWeight: '700' }}>{task.status}</span>
                    </div>
                    <div>
                        {isAdmin && (
                            <button
                                onClick={openEditTask}
                                style={{ marginLeft: '10px', background: '#eff6ff', color: '#1e40af', border: 'none', padding: '3px 10px', borderRadius: '5px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                            >
                                Edit Task
                            </button>
                        )}
                    </div>
                </div>

                {/* Top: left info + right status panel */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '24px', marginBottom: '20px' }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '14px', color: '#334155' }}>
                        <div>
                            <span style={infoLabelStyle}>Assigned To:</span>
                            {canChangeRecruiter ? (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                    <select
                                        value={recruiterChoice}
                                        onChange={(e) => setRecruiterChoice(e.target.value)}
                                        style={{ padding: '4px 8px', borderRadius: '6px', border: '1px solid #cbd5e1', fontSize: '12px' }}
                                    >
                                        <option value="">Select assignee...</option>
                                        {recruiterOptions.map(name => (
                                            <option key={name} value={name}>
                                                {name}
                                            </option>
                                        ))}
                                    </select>
                                    {recruiterChoice !== task.assigned_to && (
                                        <button
                                            onClick={handleChangeRecruiter}
                                            style={{ background: '#2563eb', color: '#fff', border: 'none', padding: '4px 10px', borderRadius: '6px', fontSize: '11px', fontWeight: '700', cursor: 'pointer' }}
                                        >
                                            Save
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', background: '#dbeafe', color: '#1d4ed8', padding: '2px 8px', borderRadius: '12px', fontWeight: 'bold', fontSize: '12px' }}>
                                    <UserCheck size={12} /> {task.assigned_to}
                                </span>
                            )}
                            {!canChangeRecruiter && (isTeamLeadOrAdmin) && (
                                <div style={{ fontSize: '11px', color: '#94a3b8', marginTop: '3px', fontStyle: 'italic' }}>
                                    This task cannot be reassigned because it is currently in progress.
                                </div>
                            )}
                        </div>
                        <div>
                            <span style={infoLabelStyle}>Task To Be Done / Task Instructions:</span>
                            <strong style={{ color: '#0f172a' }}>{task.title}</strong>
                        </div>
                        <div>
                            <span style={infoLabelStyle}>Keywords / Skills / Special Instructions</span>
                            <strong style={{ color: '#0f172a' }}>{task.instructions}</strong>
                        </div>
                        <div>
                            <span style={infoLabelStyle}>Client SPOC:</span>
                            <strong>{task.client_spoc || linkedJob?.client_spoc || '-'}</strong>
                        </div>
                        <div>
                            <span style={infoLabelStyle}>Evanti SPOC:</span>
                            <strong>{linkedJob?.evanti_spoc || '-'}</strong>
                        </div>
                        {task.submitted_date && (
                            <div>
                                <span style={infoLabelStyle}>Submitted Date:</span>
                                <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', color: '#16a34a', fontWeight: '600' }}>
                                    <CheckCircle size={14} /> {formatDisplayDate(task.submitted_date)}
                                </span>
                            </div>
                        )}
                    </div>

                    <div style={{ borderLeft: '1px solid #e2e8f0', paddingLeft: '24px', display: 'flex', flexDirection: 'column', gap: '14px' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Task Status</span>
                            <span style={{ ...statusStyle, padding: '4px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '700' }}>{task.status}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Priority</span>
                            <span style={{ ...priorityStyle, padding: '4px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '700' }}>{task.priority || 'Medium'}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600' }}>Assigned By:</span>
                            <span style={{ ...priorityStyle, padding: '4px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '700' }}>{task.assigned_by}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600', marginRight: "10px" }}>Assigned Date:</span>
                            <span style={{ ...statusStyle, padding: '4px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '700' }}>{formatDisplayDate(task.assigned_date)}</span>
                        </div>
                        {task.reassigned_date && (
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600', marginRight: "10px" }}>Reassignment:</span>
                                <span style={{ ...statusStyle, padding: '4px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '700' }}>
                                    {formatDisplayDate(task.reassigned_date)} — {task.reassigned_by} → {task.reassigned_to}
                                    {task.reassignment_count ? ` (reassigned ${task.reassignment_count}x total)` : ''}
                                </span>
                            </div>
                        )}
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600', marginRight: "10px" }}>Candidate Count:</span>
                            <span style={{ ...statusStyle, padding: '4px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '700' }}>{savedLog.length}</span>
                        </div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span style={{ fontSize: '13px', color: '#64748b', fontWeight: '600', marginRight: "10px" }}>KPI Summary:</span>
                            <span style={{ ...statusStyle, padding: '4px 12px', borderRadius: '14px', fontSize: '12px', fontWeight: '700' }}>
                                {savedLog.filter(c => (c.remark || '').toLowerCase() === 'interested').length} Interested,{' '}
                                {savedLog.filter(c => (c.remark || '').toLowerCase() === 'not interested').length} Not Interested,{' '}
                                {savedLog.filter(c => c.skill && c.email).length} With Full Details
                            </span>
                        </div>
                    </div>
                </div>

                {/* KPI & Targets — entered rows are persisted via "Save as Draft" */}
                <div style={{ ...sectionBoxStyle, marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '700', color: '#1f2937', marginBottom: '12px', fontSize: '14px' }}>
                        <TrendingUp size={16} color="#2563eb" /> KPI & Targets
                    </div>

                    {kpiError && (
                        <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 10px', borderRadius: '6px', fontSize: '12px', marginBottom: '10px' }}>
                            {kpiError}
                        </div>
                    )}

                    <div style={{ overflowX: isMobile ? 'auto' : 'visible' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '110px 100px 100px 1.1fr 1fr 1.2fr 50px', gap: '10px', fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '8px', minWidth: isMobile ? '680px' : 'auto' }}>
                            <span>Date</span><span>Task ID</span><span>Job Role</span><span>Name</span><span>Number</span><span>Remark</span><span>Action</span>
                        </div>

                        {draftRows.map(row => (
                            <div key={row.id} style={{ display: 'grid', gridTemplateColumns: '110px 100px 100px 1.1fr 1fr 1.2fr 50px', gap: '10px', marginBottom: '8px', alignItems: 'center', minWidth: isMobile ? '680px' : 'auto' }}>
                                <DateInput style={kpiLocked ? lockedTableInputStyle : tableInputStyle} value={row.entry_date} onChange={(e) => updateDraftRow(row.id, 'entry_date', e.target.value)} disabled={kpiLocked} />
                                <input style={lockedTableInputStyle} value={task.display_id} disabled readOnly />
                                <input style={lockedTableInputStyle} value={linkedJob?.job_role || task.job_role || 'N/A'} disabled readOnly />
                                <input style={kpiLocked ? lockedTableInputStyle : tableInputStyle} value={row.name} onChange={(e) => updateDraftRow(row.id, 'name', e.target.value)} placeholder="e.g. John Candidate" disabled={kpiLocked} />
                                <input style={kpiLocked ? lockedTableInputStyle : tableInputStyle} value={row.number} onChange={(e) => updateDraftRow(row.id, 'number', e.target.value)} onBlur={() => handleNumberBlur(row.id)} placeholder="Phone number" disabled={kpiLocked} />
                                <div>
                                    <select
                                        style={kpiLocked ? lockedTableInputStyle : remarkSelectStyle}
                                        value={row.remark}
                                        onChange={(e) => updateDraftRow(row.id, 'remark', e.target.value)}
                                        disabled={kpiLocked}
                                    >
                                        <option value="">Select outcome...</option>
                                        {REMARK_OPTIONS.map(opt => (
                                            <option key={opt} value={opt}>{opt}</option>
                                        ))}
                                    </select>
                                    {row.prefill && (
                                        <div style={{ fontSize: '10px', color: '#16a34a', marginTop: '2px' }}>✓ Existing details will be used</div>
                                    )}
                                </div>
                                <button
                                    onClick={() => removeDraftRow(row.id)}
                                    disabled={kpiLocked}
                                    style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: kpiLocked ? '#f3f4f6' : '#fee2e2', color: kpiLocked ? '#9ca3af' : '#b91c1c', border: '1px solid #fecaca', borderRadius: '6px', padding: '7px', cursor: kpiLocked ? 'not-allowed' : 'pointer' }}
                                >
                                    <Trash2 size={13} />
                                </button>
                            </div>
                        ))}
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '4px' }}>
                        <button
                            onClick={addDraftRow}
                            disabled={kpiLocked}
                            style={{ display: 'flex', alignItems: 'center', gap: '5px', background: 'none', border: 'none', color: kpiLocked ? '#9ca3af' : '#2563eb', fontWeight: '600', fontSize: '13px', cursor: kpiLocked ? 'not-allowed' : 'pointer', padding: '4px 0' }}
                        >
                            <Plus size={14} /> Add KPI
                        </button>

                        <button
                            onClick={handleSaveDraft}
                            disabled={savingKpi || kpiLocked}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '6px', background: kpiLocked ? '#9ca3af' : '#16a34a', color: '#fff',
                                border: 'none', padding: '8px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '700',
                                cursor: (savingKpi || kpiLocked) ? 'not-allowed' : 'pointer', opacity: savingKpi ? 0.7 : 1
                            }}
                        >
                            <Save size={14} /> {savingKpi ? 'Saving...' : 'Save as Draft'}
                        </button>
                    </div>

                    {/* Log of previously saved entries for this task */}
                    {savedLog.length > 0 && (
                        <div style={{ marginTop: '16px', borderTop: '1px solid #e2e8f0', paddingTop: '12px' }}>
                            <div style={{ fontSize: '12px', fontWeight: '700', color: '#64748b', marginBottom: '8px' }}>
                                Saved Entries ({savedLog.length})
                            </div>
                            <div style={{ overflowX: isMobile ? 'auto' : 'visible' }}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '200px', overflowY: 'auto', minWidth: isMobile ? '800px' : 'auto' }}>
                                    {savedLog.map((entry, snoIdx) => {
                                        const isInterested = (entry.remark || '').trim().toLowerCase() === 'interested';
                                        const hasRemark = !!(entry.remark && entry.remark.trim());
                                        const hasDetails = !!(entry.skill && entry.email);
                                        return (
                                            <div key={entry.id} style={{ display: 'grid', gridTemplateColumns: '32px 90px 90px 90px 1fr 0.9fr 1fr 118px 1.2fr', gap: '10px', fontSize: '12px', color: '#334155', background: '#fff', padding: '6px 8px', borderRadius: '5px', border: '1px solid #e2e8f0', alignItems: 'center' }}>
                                                <span style={{ color: '#64748b', fontWeight: '600' }}>{snoIdx + 1}</span>
                                                <span>{formatDisplayDate(entry.entry_date)}</span>
                                                <span style={{ color: '#2563eb', fontWeight: '600' }}>{task.display_id}</span>
                                                <span>{entry.job_role || linkedJob?.job_role || 'N/A'}</span>
                                                <span>{entry.name || '-'}</span>
                                                <span>{entry.number || '-'}</span>
                                                <span style={{ fontWeight: isInterested ? '700' : '400', color: isInterested ? '#166534' : '#334155' }}>
                                                    {entry.remark || '-'}
                                                </span>
                                                {/* Updated Date: stamped whenever this candidate's Remark or Number changes
                                                (from the Candidates tab). */}
                                                <span
                                                    data-testid="candidate-updated-date"
                                                    title="Last time this candidate's Remark or Number was changed"
                                                    style={{ fontSize: '11px', color: entry.updated_at ? '#1e40af' : '#94a3b8', fontWeight: entry.updated_at ? '600' : '400' }}
                                                >
                                                    {entry.updated_at ? `Updated ${formatTimestampDate(entry.updated_at)}` : 'Not updated'}
                                                </span>
                                                {entry.read_only ? (
                                                    <span data-testid="added-by" style={{ fontSize: '10px', color: '#64748b', fontStyle: 'italic' }}>
                                                        Added by {entry.created_by_name || 'previous recruiter'}
                                                    </span>
                                                ) : hasRemark && detailsUnlocked ? (
                                                    <button
                                                        onClick={() => openCandidateModal(entry)}
                                                        style={{
                                                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px',
                                                            background: hasDetails ? '#dcfce7' : '#fef3c7', color: hasDetails ? '#166534' : '#92400e',
                                                            border: 'none', padding: '4px 8px', borderRadius: '4px', fontSize: '11px', fontWeight: '700', cursor: 'pointer'
                                                        }}
                                                    >
                                                        {hasDetails ? 'Edit Details' : 'Add Details'}
                                                    </button>
                                                ) : hasRemark ? (
                                                    <span style={{ fontSize: '10px', color: '#94a3b8', fontStyle: 'italic' }}>
                                                        {isPendingStage ? 'Accept task first' : 'Locked (only editable in Work In Progress)'}
                                                    </span>
                                                ) : <span />}
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>
                        </div>
                    )}

                    <div style={{ marginTop: '10px', background: kpiLocked ? '#fef3c7' : '#eff6ff', color: kpiLocked ? '#92400e' : '#1e40af', fontSize: '12px', padding: '8px 12px', borderRadius: '6px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <Info size={13} /> {
                            isPendingStage
                                ? 'Accept this task (move it to Work In Progress) before KPI entries can be added.'
                                : kpiLocked
                                    ? 'KPI entry is locked once a task reaches Ready To Submit — no further entries can be added.'
                                    : 'Click "Save as Draft" to log these KPI entries. Unsaved rows are discarded when you close this window.'
                        }
                    </div>
                </div>

                {/* Submission form — only shown to the Recruiter this task is assigned to,
                    while it sits at Ready To Submit. Team Lead can view the task but
                    shouldn't submit on the recruiter's behalf. */}
                {isToSubmitStage && !isSubmitted && currentUser?.name === task.assigned_to && (
                    <div style={{ ...sectionBoxStyle, background: '#fffbeb', borderColor: '#fde68a', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700', color: '#92400e', marginBottom: '12px', fontSize: '14px' }}>
                            <Send size={15} /> Submit This Task
                        </div>

                        {submitError && (
                            <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 10px', borderRadius: '6px', fontSize: '12px', marginBottom: '10px' }}>
                                {submitError}
                            </div>
                        )}

                        {!hasEligibleCandidate && (
                            <div style={{ background: '#fef3c7', color: '#92400e', padding: '8px 10px', borderRadius: '6px', fontSize: '12px', marginBottom: '10px' }}>
                                Mark at least one candidate "Interested" in the KPI log above and click "Add Details" to complete their profile before submitting.
                            </div>
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px', marginBottom: '14px' }}>
                            <div>
                                <span style={infoLabelStyle}>Submission Description *</span>
                                <textarea
                                    style={textareaStyle}
                                    rows={3}
                                    value={submissionDescription}
                                    onChange={(e) => setSubmissionDescription(e.target.value)}
                                    placeholder="Describe what was done on this task..."
                                />
                            </div>
                            <div>
                                <span style={infoLabelStyle}>Submit To *</span>
                                <select
                                    style={selectStyle}
                                    value={submittedTo}
                                    onChange={(e) => setSubmittedTo(e.target.value)}
                                >
                                    <option value="">Select recipient...</option>
                                    {submitToOptions.map(opt => (
                                        <option key={opt} value={opt}>{opt}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <button
                            onClick={handleSubmitTask}
                            disabled={!canSubmit || submitting}
                            style={{
                                display: 'flex', alignItems: 'center', gap: '6px',
                                background: canSubmit ? '#16a34a' : '#9ca3af', color: '#fff',
                                border: 'none', padding: '10px 18px', borderRadius: '6px', fontSize: '13px',
                                fontWeight: '700', cursor: (!canSubmit || submitting) ? 'not-allowed' : 'pointer'
                            }}
                        >
                            <Send size={14} /> {submitting ? 'Submitting...' : 'Submit Task'}
                        </button>
                    </div>
                )}

                {/* Approval box — shown once the task has been submitted, while it awaits the selected Team Lead's approval */}
                {isSubmitted && (
                    <div style={{ ...sectionBoxStyle, background: '#eff6ff', borderColor: '#bfdbfe', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700', color: '#1e40af', marginBottom: '8px', fontSize: '14px' }}>
                            <CheckCircle size={15} /> {canApprove ? 'Awaiting Your Approval' : 'Awaiting Approval'}
                        </div>
                        <p style={{ fontSize: '13px', color: '#334155', margin: '0 0 12px' }}>
                            {task.assigned_to} submitted this task to <strong>{task.submitted_to}</strong>. Review the submission below and approve to mark it Completed.
                        </p>

                        {approveError && (
                            <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 10px', borderRadius: '6px', fontSize: '12px', marginBottom: '10px' }}>
                                {approveError}
                            </div>
                        )}

                        {canApprove ? (
                            <button
                                onClick={handleApprove}
                                disabled={approving}
                                style={{
                                    display: 'flex', alignItems: 'center', gap: '6px', background: '#16a34a', color: '#fff',
                                    border: 'none', padding: '10px 18px', borderRadius: '6px', fontSize: '13px',
                                    fontWeight: '700', cursor: approving ? 'not-allowed' : 'pointer', opacity: approving ? 0.7 : 1
                                }}
                            >
                                <CheckCircle size={14} /> {approving ? 'Approving...' : 'Approve & Mark Completed'}
                            </button>
                        ) : (
                            <div style={{ fontSize: '12px', color: '#64748b' }}>
                                Waiting on {task.submitted_to} to approve this submission.
                            </div>
                        )}
                    </div>
                )}

                {/* Admin-ended details */}
                {isAdminEnded && (
                    <div data-testid="admin-ended-details" style={{ ...sectionBoxStyle, background: '#f5f3ff', borderColor: '#6d28d9', borderWidth: '2px', borderStyle: 'solid', marginBottom: '20px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontWeight: '800', color: '#5b21b6', marginBottom: '10px', fontSize: '14px' }}>
                            <Ban size={15} /> {task?.ended_by}
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr 2fr', gap: '12px' }}>
                            <div><span style={infoLabelStyle}>Ended By</span><strong>{task.ended_by || '-'}</strong></div>
                            <div><span style={infoLabelStyle}>Ended Date & Time</span><strong>{formatEndedAt(task.ended_at)}</strong></div>
                            <div><span style={infoLabelStyle}>End Reason</span><span>{task.end_reason || 'Not provided'}</span></div>
                        </div>
                    </div>
                )}

                {/* Admin-only End Task */}
                {canEndTask && (
                    <div style={{ ...sectionBoxStyle, background: '#faf5ff', borderColor: '#e9d5ff', marginBottom: '20px' }}>
                        {!showEndForm ? (
                            <button
                                onClick={() => { setShowEndForm(true); setEndError(''); }}
                                style={{ display: 'flex', alignItems: 'center', gap: '6px', background: '#6d28d9', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '700', cursor: 'pointer' }}
                            >
                                <Ban size={14} /> End Task
                            </button>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                <span style={infoLabelStyle}>End Reason (optional)</span>
                                <textarea style={textareaStyle} rows={2} value={endReasonInput} onChange={(e) => setEndReasonInput(e.target.value)} placeholder="Why is this task being ended?" />
                                <p style={{ margin: 0, fontSize: '12px', color: '#6b7280' }}>The task moves straight to Completed. All task and reassignment history is kept.</p>
                                {endError && <div style={{ color: '#b91c1c', fontSize: '12px' }}>{endError}</div>}
                                <div style={{ display: 'flex', gap: '8px' }}>
                                    <button onClick={handleEndTask} disabled={endingTask} style={{ background: '#6d28d9', color: '#fff', border: 'none', padding: '9px 16px', borderRadius: '6px', fontSize: '13px', fontWeight: '700', cursor: endingTask ? 'not-allowed' : 'pointer' }}>
                                        {endingTask ? 'Ending...' : 'Confirm End Task'}
                                    </button>
                                    <button onClick={() => { setShowEndForm(false); setEndReasonInput(''); }} style={{ background: '#e2e8f0', color: '#334155', border: 'none', padding: '9px 14px', borderRadius: '6px', fontSize: '13px', cursor: 'pointer' }}>Cancel</button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Bottom: submission details + activity history */}
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : (task.submission_description ? '1fr 1fr' : '1fr'), gap: '20px' }}>
                    {task.submission_description && (
                        <div style={{ ...sectionBoxStyle, background: '#f0fdf4', borderColor: '#bbf7d0' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700', color: '#166534', marginBottom: '12px', fontSize: '14px' }}>
                                <CheckCircle size={15} /> Submission Details
                            </div>
                            <span style={infoLabelStyle}>Submitted To:</span>
                            <strong style={{ display: 'block', color: '#0f172a', marginBottom: '10px' }}>{task.submitted_to}</strong>
                            <span style={infoLabelStyle}>Submission Description:</span>
                            <div style={{ background: '#dcfce7', padding: '10px', borderRadius: '6px', fontSize: '13px', color: '#14532d', marginTop: '4px' }}>
                                {task.submission_description}
                            </div>
                        </div>
                    )}

                    <div style={sectionBoxStyle}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: '700', color: '#1f2937', marginBottom: '12px', fontSize: '14px' }}>
                            <Clock size={15} /> Task Activity & History
                        </div>
                        {loadingHistory ? (
                            <div style={{ fontSize: '13px', color: '#94a3b8' }}>Loading...</div>
                        ) : history.length === 0 ? (
                            <div style={{ fontSize: '13px', color: '#94a3b8' }}>No activity recorded yet.</div>
                        ) : (
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                {[...history]
                                    .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                                    .map(entry => (
                                        <div
                                            key={entry.id}
                                            data-admin-ended={entry.action_type === 'Task Ended by Admin' ? 'true' : undefined}
                                            style={{
                                                display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', fontSize: '12px',
                                                ...(entry.action_type === 'Task Ended by Admin' ? { background: '#f5f3ff', border: '1px solid #6d28d9', borderRadius: '6px', padding: '6px 8px' } : {})
                                            }}
                                        >
                                            <div>
                                                {entry.action_type === 'Task Ended by Admin' && (
                                                    <span style={{ display: 'inline-block', background: '#6d28d9', color: '#fff', fontSize: '9px', fontWeight: '800', letterSpacing: '0.04em', padding: '2px 7px', borderRadius: '10px', marginBottom: '3px' }}>{task?.ended_by}</span>
                                                )}
                                                <div style={{ fontWeight: '700', color: '#1f2937' }}>{entry.action}</div>
                                                <div style={{ color: '#64748b' }}>By {entry.actor}</div>
                                            </div>
                                            <div style={{ color: '#94a3b8', whiteSpace: 'nowrap', fontSize: '11px', textAlign: 'right' }}>
                                                {formatEndedAt(entry.created_at)}
                                            </div>
                                        </div>
                                    ))}
                            </div>
                        )}
                    </div>
                </div>

                <button onClick={handleClose} style={{ width: '100%', background: '#007bff', color: '#fff', border: 'none', padding: '12px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer', marginTop: '20px' }}>
                    Close Details
                </button>
            </div>

            {/* Candidate Details modal — only ever opened for a KPI row marked "Interested" */}
            {candidateModalRow && candidateForm && (
                <div
                    onClick={closeCandidateModal}
                    style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100, padding: '20px', boxSizing: 'border-box' }}
                >
                    <div
                        onClick={(e) => e.stopPropagation()}
                        style={{ background: '#fff', borderRadius: '12px', padding: isMobile ? '16px' : '24px', width: '100%', maxWidth: isMobile ? '95vw' : '560px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 10px 25px rgba(0,0,0,0.25)', position: 'relative', boxSizing: 'border-box' }}
                    >
                        <button onClick={closeCandidateModal} style={{ position: 'absolute', top: '16px', right: '16px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#666' }}>
                            <X size={18} />
                        </button>

                        <h3 style={{ margin: '0 0 4px', fontSize: '15px', fontWeight: '700', color: '#1f2937' }}>
                            Candidate Details — {candidateModalRow.name || 'Unnamed'}
                        </h3>
                        <p style={{ margin: '0 0 16px', fontSize: '12px', color: '#64748b' }}>
                            Marked Interested on {formatDisplayDate(candidateModalRow.entry_date)}
                        </p>

                        {candidateError && (
                            <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 10px', borderRadius: '6px', fontSize: '12px', marginBottom: '14px' }}>
                                {candidateError}
                            </div>
                        )}

                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px', marginBottom: '14px' }}>
                            <div style={cdFieldWrap}>
                                <label style={cdLabelStyle}>
                                    Skill * {linkedJob && <span style={{ color: '#94a3b8', fontWeight: '400' }}>(from linked job)</span>}
                                </label>
                                <input style={cdFieldStyle} value={candidateForm.skill} onChange={handleCandidateFieldChange('skill')} placeholder="e.g. React, Node.js" />
                            </div>
                            <div style={cdFieldWrap}>
                                <label style={cdLabelStyle}>Email *</label>
                                <input style={cdFieldStyle} type="email" value={candidateForm.email} onChange={handleCandidateFieldChange('email')} placeholder="candidate@email.com" />
                            </div>
                            <div style={cdFieldWrap}>
                                <label style={cdLabelStyle}>Location</label>
                                <input style={cdFieldStyle} value={candidateForm.location} onChange={handleCandidateFieldChange('location')} />
                            </div>
                            <div style={cdFieldWrap}>
                                <label style={cdLabelStyle}>Current Employer</label>
                                <input style={cdFieldStyle} value={candidateForm.current_employer} onChange={handleCandidateFieldChange('current_employer')} />
                            </div>
                            <div style={cdFieldWrap}>
                                <label style={cdLabelStyle}>Designation</label>
                                <input style={cdFieldStyle} value={candidateForm.designation} onChange={handleCandidateFieldChange('designation')} />
                            </div>
                            <div style={cdFieldWrap}>
                                <label style={cdLabelStyle}>Total Experience (Years)</label>
                                <input style={cdFieldStyle} type="number" step="0.1" min="0" value={candidateForm.total_experience_years} onChange={handleCandidateFieldChange('total_experience_years')} />
                            </div>
                            <div style={cdFieldWrap}>
                                <label style={cdLabelStyle}>Relevant Experience (Years)</label>
                                <input style={cdFieldStyle} type="number" step="0.1" min="0" value={candidateForm.relevant_experience_years} onChange={handleCandidateFieldChange('relevant_experience_years')} />
                            </div>
                            <div style={cdFieldWrap}>
                                <label style={cdLabelStyle}>Notice Period</label>
                                <input style={cdFieldStyle} value={candidateForm.notice_period} onChange={handleCandidateFieldChange('notice_period')} placeholder="e.g. 30 days" />
                            </div>
                            <div style={cdFieldWrap}>
                                <label style={cdLabelStyle}>Education</label>
                                <input style={cdFieldStyle} value={candidateForm.education} onChange={handleCandidateFieldChange('education')} />
                            </div>
                            <div style={cdFieldWrap}>
                                <label style={cdLabelStyle}>
                                    Vendor Name {linkedJob && <span style={{ color: '#94a3b8', fontWeight: '400' }}>(from linked job)</span>}
                                </label>
                                <input style={cdFieldStyle} value={candidateForm.vendor_name} onChange={handleCandidateFieldChange('vendor_name')} />
                            </div>
                            <div style={cdFieldWrap}>
                                <label style={cdLabelStyle}>
                                    Client SPOC {linkedJob && <span style={{ color: '#94a3b8', fontWeight: '400' }}>(from linked job)</span>}
                                </label>
                                <input style={cdFieldStyle} list="cd-client-spoc-options" value={candidateForm.client_spoc} onChange={handleCandidateFieldChange('client_spoc')} />
                                <datalist id="cd-client-spoc-options">
                                    {spocsFromCompanies(companyMaster, task?.company).map(name => <option key={name} value={name} />)}
                                </datalist>
                            </div>
                            <div style={cdFieldWrap}>
                                <label style={cdLabelStyle}>
                                    Evanti SPOC {linkedJob && <span style={{ color: '#94a3b8', fontWeight: '400' }}>(from linked job)</span>}
                                </label>
                                <input style={cdFieldStyle} value={candidateForm.evanti_spoc} onChange={handleCandidateFieldChange('evanti_spoc')} />
                            </div>
                            <div style={cdFieldWrap}>
                                <label style={cdLabelStyle}>
                                    Evanti Pay / Bill Rate {linkedJob?.bill_rate && <span style={{ color: '#94a3b8', fontWeight: '400' }}>(from linked job)</span>}
                                </label>
                                <input style={cdFieldStyle} value={candidateForm.bill_rate} onChange={handleCandidateFieldChange('bill_rate')} placeholder="e.g. ₹850/hr" />
                            </div>
                            <div style={cdFieldWrap}>
                                <label style={cdLabelStyle}>Current Package</label>
                                <input style={cdFieldStyle} value={candidateForm.current_package} onChange={handleCandidateFieldChange('current_package')} placeholder="e.g. 8 LPA" />
                            </div>
                            <div style={cdFieldWrap}>
                                <label style={cdLabelStyle}>Expected Package</label>
                                <input style={cdFieldStyle} value={candidateForm.expected_package} onChange={handleCandidateFieldChange('expected_package')} placeholder="e.g. 12 LPA" />
                            </div>
                        </div>

                        <div style={cdFieldWrap}>
                            <label style={cdLabelStyle}>Remarks</label>
                            <textarea
                                style={{ ...cdFieldStyle, resize: 'vertical' }}
                                rows={2}
                                value={candidateForm.details_remarks}
                                onChange={handleCandidateFieldChange('details_remarks')}
                                placeholder="Additional notes about this candidate..."
                            />
                        </div>

                        <button
                            onClick={handleSaveCandidateDetails}
                            disabled={savingCandidate}
                            style={{
                                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '6px',
                                background: '#16a34a', color: '#fff', border: 'none', padding: '11px', borderRadius: '6px',
                                fontWeight: '700', fontSize: '13px', cursor: savingCandidate ? 'not-allowed' : 'pointer',
                                opacity: savingCandidate ? 0.7 : 1, marginTop: '16px'
                            }}
                        >
                            <Save size={14} /> {savingCandidate ? 'Saving...' : 'Save Candidate Details'}
                        </button>
                    </div>
                </div>
            )}

            {showJobDetails && (
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.55)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1100, padding: '20px', boxSizing: 'border-box' }}>
                    <div style={{ background: '#fff', borderRadius: '12px', padding: isMobile ? '16px' : '24px', width: '100%', maxWidth: isMobile ? '95vw' : '560px', maxHeight: '85vh', overflowY: 'auto', boxShadow: '0 10px 25px rgba(0,0,0,0.25)', position: 'relative', boxSizing: 'border-box' }}>
                        <button onClick={() => setShowJobDetails(false)} style={{ position: 'absolute', top: '16px', right: '16px', background: 'transparent', border: 'none', cursor: 'pointer', color: '#666' }}>
                            <X size={18} />
                        </button>
                        <h3 style={{ margin: '0 0 4px', fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>Job Requirement Details</h3>
                        {!linkedJob ? (
                            <p style={{ color: '#94a3b8', fontSize: '13px', marginTop: '16px' }}>Loading job details...</p>
                        ) : (
                            <>
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px', margin: '10px 0 16px', flexWrap: 'wrap' }}>
                                    <span style={{ fontWeight: '700', color: '#2563eb', fontSize: '15px' }}>{linkedJob.job_id}</span>
                                    {linkedJob.req_id && (
                                        <span style={{ fontSize: '12px', color: '#64748b', background: '#f1f5f9', padding: '3px 10px', borderRadius: '12px', fontWeight: '600' }}>
                                            Req ID: {linkedJob.req_id}
                                        </span>
                                    )}
                                    <span style={{ background: '#dbeafe', color: '#1e40af', padding: '3px 10px', borderRadius: '12px', fontSize: '11px', fontWeight: '700' }}>{linkedJob.status}</span>
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px', fontSize: '13px' }}>
                                    <div><span style={infoLabelStyle}>Job Role</span><span>{linkedJob.job_role}</span></div>
                                    <div><span style={infoLabelStyle}>Company</span><span>{linkedJob.company_name}</span></div>
                                    <div><span style={infoLabelStyle}>Client SPOC</span><span>{linkedJob.client_spoc || '-'}</span></div>
                                    <div><span style={infoLabelStyle}>Evanti SPOC</span><span>{linkedJob.evanti_spoc || '-'}</span></div>
                                    <div><span style={infoLabelStyle}>Experience</span><span>{linkedJob.experience || '-'}</span></div>
                                    <div><span style={infoLabelStyle}>Candidate Budget</span><span>{linkedJob.candidate_budget || '-'}</span></div>
                                    <div><span style={infoLabelStyle}>Client Budget</span><span>{linkedJob.client_budget || '-'}</span></div>
                                    <div><span style={infoLabelStyle}>Evanti Pay / Bill Rate</span><span>{linkedJob.bill_rate || '-'}</span></div>
                                    <div><span style={infoLabelStyle}>Positions</span><span>{linkedJob.positions}</span></div>
                                    <div><span style={infoLabelStyle}>Location</span><span>{linkedJob.location || '-'}</span></div>
                                </div>
                                <div style={{ marginTop: '16px' }}>
                                    <span style={infoLabelStyle}>Job Description</span>
                                    <div style={{ fontSize: '13px', color: '#334155', whiteSpace: 'pre-wrap', background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: '8px', padding: '10px', marginTop: '4px', maxHeight: '200px', overflowY: 'auto' }}>
                                        {linkedJob.job_description || 'No description provided.'}
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                </div>
            )}

            {showEditTask && (
                // Item #5: Admin-only edit — every field except Task ID, which is
                // shown here read-only as a reminder it's permanently locked.
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1300, padding: '20px', boxSizing: 'border-box' }}>
                    <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '480px', boxShadow: '0 10px 25px rgba(0,0,0,0.25)' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                            <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>Edit Task</h3>
                            <button onClick={() => setShowEditTask(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={18} /></button>
                        </div>

                        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                            <div>
                                <label style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', display: 'block', marginBottom: '3px' }}>Task ID (locked)</label>
                                <input value={task.display_id} disabled style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #e2e8f0', background: '#f8fafc', color: '#94a3b8', boxSizing: 'border-box' }} />
                            </div>
                            <div>
                                <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '3px' }}>Task Title</label>
                                <input value={editTaskForm.title} onChange={(e) => setEditTaskForm(prev => ({ ...prev, title: e.target.value }))} style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                            </div>
                            <div>
                                <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '3px' }}>Company</label>
                                <input value={editTaskForm.company} onChange={(e) => handleEditCompanyChange(e.target.value)} style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                            </div>
                            <div>
                                <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '3px' }}>Job Role</label>
                                <select
                                    value={editTaskForm.job_role}
                                    onChange={(e) => handleEditJobRoleChange(e.target.value)}
                                    disabled={!editTaskForm.company}
                                    style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box', background: '#fff' }}
                                >
                                    <option value="">{editTaskForm.company ? 'Select Job Role...' : 'Enter a Company first'}</option>
                                    {editTaskForm.job_role && !jobRoleOptionsForEdit.some(j => j.job_role === editTaskForm.job_role) && (
                                        <option value={editTaskForm.job_role}>{editTaskForm.job_role} (current — not an Open job)</option>
                                    )}
                                    {jobRoleOptionsForEdit.map(j => (
                                        <option key={j.job_id} value={j.job_role}>{j.job_role} ({j.job_id})</option>
                                    ))}
                                </select>
                                <span style={{ fontSize: '10px', color: '#94a3b8', marginTop: '3px', display: 'block' }}>
                                    Only Open jobs at this company can be selected. Picking one also updates Company and Client SPOC to match.
                                </span>
                            </div>
                            <div>
                                <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '3px' }}>Client SPOC</label>
                                <input value={editTaskForm.client_spoc} onChange={(e) => setEditTaskForm(prev => ({ ...prev, client_spoc: e.target.value }))} style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                            </div>

                            <div>
                                <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '3px' }}>Assign Date</label>
                                <DateInput value={editTaskForm.assigned_date} onChange={(e) => setEditTaskForm(prev => ({ ...prev, assigned_date: e.target.value }))} min={toYMD(new Date())} style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box' }} />
                            </div>
                            <div>
                                <label style={{ fontSize: '11px', fontWeight: '700', color: '#475569', display: 'block', marginBottom: '3px' }}>Instructions</label>
                                <textarea value={editTaskForm.instructions} onChange={(e) => setEditTaskForm(prev => ({ ...prev, instructions: e.target.value }))} rows={3} style={{ width: '100%', padding: '8px 10px', borderRadius: '6px', border: '1px solid #cbd5e1', boxSizing: 'border-box', resize: 'vertical' }} />
                            </div>

                            {editTaskError && (
                                <div style={{ background: '#fee2e2', color: '#b91c1c', padding: '8px 12px', borderRadius: '6px', fontSize: '12px' }}>{editTaskError}</div>
                            )}

                            <div style={{ display: 'flex', gap: '10px', marginTop: '4px' }}>
                                <button onClick={() => setShowEditTask(false)} style={{ flex: 1, background: '#f1f5f9', color: '#334155', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}>Cancel</button>
                                <button
                                    onClick={handleSaveEditTask}
                                    disabled={savingEditTask}
                                    style={{ flex: 1, background: '#2563eb', color: '#fff', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: '700', cursor: savingEditTask ? 'not-allowed' : 'pointer', opacity: savingEditTask ? 0.7 : 1 }}
                                >
                                    {savingEditTask ? 'Saving...' : 'Save Changes'}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {duplicateCheck && (
                // Item #5: same person (Name + Number) already exists under a
                // different Job Role. Offer to carry their details over — Skill
                // is deliberately excluded, since it's specific to each role.
                <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.6)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1200, padding: '20px', boxSizing: 'border-box' }}>
                    <div style={{ background: '#fff', borderRadius: '12px', padding: '24px', width: '100%', maxWidth: '440px', boxShadow: '0 10px 25px rgba(0,0,0,0.25)' }}>
                        <h3 style={{ margin: '0 0 8px', fontSize: '16px', fontWeight: '700', color: '#1f2937' }}>Existing Candidate Found</h3>
                        <p style={{ margin: '0 0 16px', fontSize: '13px', color: '#475569', lineHeight: '1.5' }}>
                            This Name + Number is already on record for <strong>{duplicateCheck.existing_job_role || 'another role'}</strong> at{' '}
                            <strong>{duplicateCheck.existing_company}</strong> (Task {duplicateCheck.existing_task_id}).
                            You can auto-fill their other details for this Job Role — a new Candidate ID is always created,
                            and this stays a separate record. Skill is not carried over; it starts fresh here.
                        </p>
                        <div style={{ display: 'flex', gap: '10px' }}>
                            <button
                                onClick={handleSkipDuplicateDetails}
                                style={{ flex: 1, background: '#f1f5f9', color: '#334155', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: '600', cursor: 'pointer' }}
                            >
                                Enter Fresh
                            </button>
                            <button
                                onClick={handleUseDuplicateDetails}
                                style={{ flex: 1, background: '#2563eb', color: '#fff', border: 'none', padding: '10px', borderRadius: '6px', fontWeight: '700', cursor: 'pointer' }}
                            >
                                Use Existing Details
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default TaskDetailModel;